import consumer from "./consumer"

console.log("Loading NotificationsChannel...");

// Avoid duplicate subscriptions across Turbo visits
if (consumer && consumer.subscriptions) {
  const existing = consumer.subscriptions.subscriptions.find((s) => {
    try {
      const identifier = JSON.parse(s.identifier)
      return identifier.channel === "NotificationsChannel"
    } catch(_) { return false }
  })
  if (existing) {
    try { console.debug("NotificationsChannel subscription already exists; skipping create") } catch(_) {}
  }
}

// Only create if there isn't one already
const ensureSubscription = () => {
  const hasSubscription = consumer.subscriptions.subscriptions.some((s) => {
    try { return JSON.parse(s.identifier).channel === "NotificationsChannel" } catch(_) { return false }
  })
  if (hasSubscription) return

  consumer.subscriptions.create("NotificationsChannel", {
  connected() {
    console.log("✅ NotificationsChannel connected successfully!");
  },

  disconnected() {
    console.log("❌ NotificationsChannel disconnected");
  },

  received(data) {
    // Handle different message types
    if (data.type === 'refresh_notifications') {
      this.refreshNotificationCount(data.count);
      return; // Don't show popup for refresh signals
    }
    
    // Only show popups for actual notifications (with title/body)
    if (!data.title && !data.body) {
      return;
    }
    
    const title = data.title || "Reminder";
    const body = data.body || "You have a new reminder.";
    const notificationId = data.notification_id;
    
    // Check if this notification has already been shown as a popup
    if (notificationId && this.hasBeenShownAsPopup(notificationId)) {
      // Already shown, just mark as delivered so it appears in the list
      this.markAsDelivered(notificationId);
      return;
    }
    
    // Show the notification using our helper method with quick actions
    this.showNotification(title, body, "info", notificationId, data.contact_id);
    
    // Try to show browser notification
    this.showBrowserNotification(title, body);
    
    // Mark this notification as shown in popup
    if (notificationId) {
      this.markAsShownInPopup(notificationId);
    }
    
    // Always mark notification as delivered so it appears in the list
    if (notificationId) {
      this.markAsDelivered(notificationId);
    }
  },
  
  // Update notification count in UI
  refreshNotificationCount(count) {
    const badge = document.querySelector('.notification-badge');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'inline-block' : 'none';
    }
    
    // Update tab badge if it exists
    const tabBadge = document.querySelector('.notification-tab__badge');
    if (tabBadge) {
      tabBadge.textContent = count;
      tabBadge.style.display = count > 0 ? 'inline-block' : 'none';
    }
  },
  
  // Check if a notification has already been shown as a popup
  hasBeenShownAsPopup(notificationId) {
    try {
      const shownNotifications = JSON.parse(localStorage.getItem('shown_notification_popups') || '[]');
      return shownNotifications.includes(notificationId.toString());
    } catch (e) {
      console.error('Error checking shown notifications:', e);
      return false;
    }
  },
  
  // Mark a notification as shown in popup
  markAsShownInPopup(notificationId) {
    try {
      const shownNotifications = JSON.parse(localStorage.getItem('shown_notification_popups') || '[]');
      
      // Add this notification ID if not already there
      if (!shownNotifications.includes(notificationId.toString())) {
        shownNotifications.push(notificationId.toString());
        
        // Keep only last 1000 notification IDs to prevent localStorage from growing too large
        if (shownNotifications.length > 1000) {
          shownNotifications.shift();
        }
        
        localStorage.setItem('shown_notification_popups', JSON.stringify(shownNotifications));
      }
    } catch (e) {
      console.error('Error marking notification as shown:', e);
    }
  },

  // Helper method to show in-page notifications
  showNotification(title, body, type = "info", notificationId = null, contactId = null) {
    
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#4CAF50' : type === 'info' ? '#2196F3' : '#FF9800'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      max-width: 350px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      animation: slideIn 0.3s ease-out;
    `;
    
    const quickActionsHTML = notificationId ? `
      <div style="display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap;">
        ${contactId ? `<button class="notification-quick-action" data-action="view-contact" data-contact-id="${contactId}" style="
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
        ">View Contact</button>` : ''}
        <button class="notification-quick-action" data-action="snooze-1h" data-notification-id="${notificationId}" style="
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
        ">Snooze 1h</button>
        <button class="notification-quick-action" data-action="snooze-1d" data-notification-id="${notificationId}" style="
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
        ">Snooze 1d</button>
        <button class="notification-quick-action" data-action="snooze-2d" data-notification-id="${notificationId}" style="
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
        ">Snooze 2d</button>
        <button class="notification-quick-action" data-action="mark-read" data-notification-id="${notificationId}" style="
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
        ">Mark Read</button>
      </div>
    ` : '';
    
    notification.innerHTML = `
      <div style="position: relative;">
        <div class="notification-progress" style="
          position: absolute;
          bottom: 0;
          left: 0;
          height: 4px;
          background: linear-gradient(90deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.6) 100%);
          border-radius: 0 0 8px 8px;
          width: 100%;
          transform-origin: left;
          animation: notificationCountdown 10s linear forwards;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        "></div>
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <div style="flex: 1;">
            <strong>${title}</strong><br>
            ${body}
          </div>
          <div style="display: flex; align-items: center; gap: 8px; margin-left: 12px;">
            <span class="notification-timer" style="
              font-size: 11px;
              opacity: 0.8;
              min-width: 20px;
              text-align: center;
              font-weight: 600;
              background: rgba(0, 0, 0, 0.1);
              padding: 2px 6px;
              border-radius: 10px;
              transition: all 0.2s ease;
            ">10s</span>
            <button class="notification-close-btn" style="
              background: none;
              border: none;
              color: white;
              font-size: 18px;
              cursor: pointer;
              padding: 0;
              margin-left: 12px;
              flex-shrink: 0;
            ">&times;</button>
          </div>
        </div>
        ${quickActionsHTML}
      </div>
    `;
    
    // Handle quick action clicks
    notification.addEventListener('click', (e) => {
      const action = e.target.dataset.action;
      
      if (action === 'view-contact') {
        const contactId = e.target.dataset.contactId;
        if (contactId) {
          window.location.href = `/home/contacts/${contactId}`;
        }
      } else if (action === 'snooze-1h') {
        const notifId = e.target.dataset.notificationId;
        if (notifId) {
          this.snoozeNotification(notifId, 1, 0);
          clearTimeout(autoRemoveTimeout);
          clearInterval(timerInterval);
          notification.remove();
        }
      } else if (action === 'snooze-1d') {
        const notifId = e.target.dataset.notificationId;
        if (notifId) {
          this.snoozeNotification(notifId, 0, 1);
          clearTimeout(autoRemoveTimeout);
          clearInterval(timerInterval);
          notification.remove();
        }
      } else if (action === 'snooze-2d') {
        const notifId = e.target.dataset.notificationId;
        if (notifId) {
          this.snoozeNotification(notifId, 0, 2);
          clearTimeout(autoRemoveTimeout);
          clearInterval(timerInterval);
          notification.remove();
        }
      } else if (action === 'mark-read') {
        const notifId = e.target.dataset.notificationId;
        if (notifId) {
          this.markAsRead(notifId);
          clearTimeout(autoRemoveTimeout);
          clearInterval(timerInterval);
          notification.remove();
        }
      }
    });
    
    // Handle close button click
    const closeBtn = notification.querySelector('.notification-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        clearTimeout(autoRemoveTimeout);
        clearInterval(timerInterval);
        notification.remove();
      });
    }
    
    document.body.appendChild(notification);
    
    // Start countdown timer with pause/resume functionality
    const timerElement = notification.querySelector('.notification-timer');
    const progressBar = notification.querySelector('.notification-progress');
    let timeLeft = 10;
    let isPaused = false;
    let timerInterval;
    let autoRemoveTimeout;
    
    const updateTimer = () => {
      if (!isPaused) {
        timeLeft--;
        if (timerElement && timeLeft > 0) {
          timerElement.textContent = `${timeLeft}s`;
          
          // Add urgency effects for final seconds
          if (timeLeft <= 3) {
            notification.style.animation = 'notificationUrgentPulse 0.8s ease-in-out infinite';
            timerElement.style.color = '#ff4444';
            timerElement.style.fontWeight = '700';
            progressBar.style.background = 'linear-gradient(90deg, #ff6b6b 0%, #ff4444 100%)';
          } else if (timeLeft <= 5) {
            timerElement.style.color = '#ff8800';
            progressBar.style.background = 'linear-gradient(90deg, #ffaa44 0%, #ff8800 100%)';
          }
        } else if (timerElement && timeLeft === 0) {
          timerElement.textContent = '';
        }
      }
    };
    
    const startTimer = () => {
      timerInterval = setInterval(updateTimer, 1000);
      autoRemoveTimeout = setTimeout(() => {
        clearInterval(timerInterval);
        if (notification && notification.parentNode) {
          notification.style.animation = 'notificationSlideOut 0.3s ease-in forwards';
          setTimeout(() => {
            if (notification && notification.parentNode) {
              notification.remove();
            }
          }, 300);
        }
      }, timeLeft * 1000);
    };
    
    const pauseTimer = () => {
      isPaused = true;
      if (progressBar) {
        progressBar.style.animationPlayState = 'paused';
      }
      clearTimeout(autoRemoveTimeout);
      clearInterval(timerInterval);
      if (timerElement) {
        timerElement.style.opacity = '0.5';
        timerElement.title = 'Timer paused - move mouse away to resume';
      }
    };
    
    const resumeTimer = () => {
      isPaused = false;
      if (progressBar) {
        progressBar.style.animationPlayState = 'running';
      }
      if (timerElement) {
        timerElement.style.opacity = '0.8';
        timerElement.title = '';
      }
      startTimer();
    };
    
    // Add hover listeners for pause/resume
    notification.addEventListener('mouseenter', pauseTimer);
    notification.addEventListener('mouseleave', resumeTimer);
    
    // Start the initial timer
    startTimer();
    

  },

  // Mark notification as delivered (successfully received via ActionCable)
  markAsDelivered(notificationId) {
    fetch(`/notifications/${notificationId}/mark_delivered`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({})
    }).then(response => {
      if (response.ok) {
        console.log(`📤 Notification ${notificationId} marked as delivered`);
      }
    }).catch(error => {
      console.error('❌ Failed to mark notification as delivered:', error);
    });
  },

  // Mark notification as read (user clicked/interacted with it)
  markAsRead(notificationId) {
    fetch(`/notifications/${notificationId}/mark_read`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({})
    }).then(response => {
      if (response.ok) {
        console.log(`👀 Notification ${notificationId} marked as read`);
      }
    }).catch(error => {
      console.error('❌ Failed to mark notification as read:', error);
    });
  },

  // Snooze a notification by N hours (or days if options specify)
  snoozeNotification(notificationId, hours = 1, days = 0) {
    const body = {};
    if (days && days > 0) {
      body.snooze_days = days;
    } else if (hours && hours > 0) {
      body.snooze_hours = hours;
    }

    fetch(`/notifications/${notificationId}/snooze`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(body)
    }).then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        console.log(`😴 Notification ${notificationId} snoozed until ${data.snoozed_until} (${data.display_time})`);
      }
    }).catch(error => {
      console.error('❌ Failed to snooze notification:', error);
    });
  },

  // Helper method for browser notifications
  showBrowserNotification(title, body) {
    if (!("Notification" in window)) {
      console.log("Browser notifications not supported");
      return;
    }

    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/icon.png' });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          new Notification(title, { body, icon: '/icon.png' });
        }
      });
    }
  },

  });
}

ensureSubscription()

// Add CSS animation for notifications
if (!document.getElementById('notification-styles')) {
  const style = document.createElement('style');
  style.id = 'notification-styles';
  style.textContent = `
    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
    
    @keyframes notificationSlideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }
    
    @keyframes notificationCountdown {
      from {
        transform: scaleX(1);
        opacity: 0.9;
      }
      to {
        transform: scaleX(0);
        opacity: 0.3;
      }
    }
    
    @keyframes notificationUrgentPulse {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      }
      50% {
        transform: scale(1.02);
        box-shadow: 0 6px 12px rgba(255, 68, 68, 0.3);
      }
    }
    
    .notification-quick-action:hover {
      background: rgba(255,255,255,0.3) !important;
      transform: scale(1.05);
    }
    
    .notification-timer:hover {
      opacity: 1 !important;
      background: rgba(0, 0, 0, 0.2) !important;
    }
  `;
  document.head.appendChild(style);
}